import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ForbiddenIcon, CheckIcon } from '@rsos/assets/icons';
import { Button, Text } from '@rsos/components/capstone/base';
import {
  ACCEPTED,
  DECLINED,
  IGNORED,
  TIMEOUT,
} from '@rsos/constants/alertStatuses';
import {
  addFollower,
  updateAlertStatus,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { RootState, useAppDispatch } from '../../app';
import { trackAlertChatButtonClick } from './alertChatBoxTracking';

interface AcceptDeclineAlertProps {
  onShowDeclinePane: (show: boolean) => void;
}

const AcceptDeclineAlert = ({ onShowDeclinePane }: AcceptDeclineAlertProps) => {
  const dispatch = useAppDispatch();

  const { currentOrg, profile } = useSelector(
    (state: RootState) => state.sinatra.user,
  );
  const userID = profile?.id;

  const isAlertStatusLoading = useSelector(
    (state: RootState) => state.incidents.loading.alertStatus,
  );

  const irpVersion = useSelector(
    (state: RootState) => state.sinatra.irp.irpVersion,
  );

  const currentAlert = useSelectedAlert();
  const isTestAlert = !!currentAlert?.test_mode?.test_enabled;

  const selectedAlertID = currentAlert?.alert_id;

  const alertStatus = currentAlert?.status?.name;

  const isSupplementalOnly = currentAlert?.supplemental_only;

  const isButtonDisabled = useMemo(
    () =>
      (alertStatus !== undefined &&
        [DECLINED, IGNORED, TIMEOUT].includes(alertStatus)) ||
      isAlertStatusLoading,
    [alertStatus, isAlertStatusLoading],
  );

  const handleAcceptAlert = () => {
    const data = {
      alertID: selectedAlertID,
      org: currentOrg?.name,
      status: ACCEPTED,
    };

    dispatch(updateAlertStatus(data));

    if (userID) dispatch(addFollower(userID));

    trackAlertChatButtonClick({
      buttonText: ACCEPTED,
      isSupplemental: isSupplementalOnly,
      selectedAlertID,
      irpVersion,
    });
  };

  const handleDeclineAlert = () => {
    onShowDeclinePane(true);

    trackAlertChatButtonClick({
      buttonText: DECLINED,
      isSupplemental: isSupplementalOnly,
      selectedAlertID,
      irpVersion,
    });
  };

  return (
    <>
      <Button
        btnType="primary"
        disabled={isButtonDisabled}
        name="alarm-accept-btn"
        onClick={handleAcceptAlert}
        size="small"
      >
        <CheckIcon width={20} height={20} />
        <Text inline size="small">
          {isTestAlert ? 'Accept Test' : 'Accept'}
        </Text>
      </Button>
      <Button
        btnType="tertiary"
        disabled={isButtonDisabled}
        name="alarm-decline-btn"
        onClick={handleDeclineAlert}
        size="small"
      >
        <ForbiddenIcon width={16} height={16} />
        <Text size="small"> {isTestAlert ? 'Decline Test' : 'Decline'}</Text>
      </Button>
    </>
  );
};

export default AcceptDeclineAlert;
